import React, { useCallback, useEffect, useState } from "react";
import {
    Building2, CheckCircle2, XCircle, AlertTriangle, Loader2, RefreshCw,
    Globe, Mail, Award, Ban,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Admin Employer Approvals — LinkedIn-style workspace verification queue.
 *
 *  - Employer OWNER self-signups arrive here with status="pending_approval"
 *    whenever their email domain does not match their claimed company website
 *    OR the email is on a free-provider (gmail, outlook, etc.). Corporate
 *    signups with a matching domain skip the queue and land active.
 *  - Team members invited by an already-approved Owner never appear here.
 *  - "Approve" flips status → active + fires the branded welcome email.
 *  - "Reject" sets status="rejected" + drops sessions + fires the notice
 *    email with the reviewer's reason.
 */

const Badge = ({ tone, icon: Icon, children }) => {
    const tones = {
        ok:    "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30",
        warn:  "bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30",
        muted: "bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-500/30",
        gold:  "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/40",
    };
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tones[tone]}`}>
            {Icon && <Icon className="h-3 w-3" />}
            {children}
        </span>
    );
};

const REASON_LABEL = {
    email_domain_mismatch: "Email domain doesn't match the company website",
    free_email_provider:   "Personal / free-email provider — needs manual check",
    no_website_provided:   "No company website provided",
};

const EmployerCard = ({ row, onAction, busy }) => {
    const created = row.created_at ? new Date(row.created_at).toLocaleString() : "—";
    const match = row.employer_domain_match;
    const free = row.email_is_free_provider;

    return (
        <div
            data-testid={`emp-approval-row-${row.user_id}`}
            className="rounded-2xl border border-border bg-card p-5 shadow-sm"
        >
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold text-foreground">{row.name || row.email}</h3>
                        <Badge tone="muted">Pending</Badge>
                        {free ? (
                            <Badge tone="warn" icon={Ban}>Free-email signup</Badge>
                        ) : row.pending_reason === "no_website_provided" ? (
                            <Badge tone="warn" icon={Globe}>No website</Badge>
                        ) : match ? (
                            <Badge tone="ok" icon={CheckCircle2}>Domain match</Badge>
                        ) : (
                            <Badge tone="warn" icon={AlertTriangle}>Domain mismatch</Badge>
                        )}
                        {row.founding_rank && row.founding_rank <= 10 && (
                            <Badge tone="gold" icon={Award}>Founding #{row.founding_rank}/10</Badge>
                        )}
                    </div>
                    <div className="mt-2 grid gap-1.5 text-sm text-muted-foreground">
                        <div className="flex flex-wrap items-center gap-2">
                            <Mail className="h-3.5 w-3.5" />
                            <span className="font-medium text-foreground">{row.email}</span>
                            {row.email_domain && (
                                <span className="text-xs text-muted-foreground">({row.email_domain})</span>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Building2 className="h-3.5 w-3.5" />
                            <span className="text-foreground">{row.company_name || <em>no company name</em>}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Globe className="h-3.5 w-3.5" />
                            <span>
                                {row.employer_website ? (
                                    <a
                                        href={row.employer_website}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                        className="underline text-foreground hover:text-emerald-700"
                                    >
                                        {row.employer_website}
                                    </a>
                                ) : (
                                    <em className="text-xs">no website provided</em>
                                )}
                            </span>
                            {row.employer_domain && (
                                <span className="text-xs text-muted-foreground">({row.employer_domain})</span>
                            )}
                        </div>
                        {row.pending_reason && (
                            <div className="text-xs text-amber-700 dark:text-amber-300">
                                Flag: {REASON_LABEL[row.pending_reason] || row.pending_reason}
                            </div>
                        )}
                        <div className="text-xs text-muted-foreground">Requested {created}</div>
                    </div>
                </div>

                <div className="flex gap-2 shrink-0">
                    <button
                        onClick={() => onAction(row, "reject")}
                        disabled={busy}
                        data-testid={`emp-approval-reject-${row.user_id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-white dark:bg-transparent px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-500/10 disabled:opacity-60"
                    >
                        <XCircle className="h-3.5 w-3.5" /> Reject
                    </button>
                    <button
                        onClick={() => onAction(row, "approve")}
                        disabled={busy}
                        data-testid={`emp-approval-approve-${row.user_id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                    >
                        <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                    </button>
                </div>
            </div>
        </div>
    );
};

export default function EmployerApprovals() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/employer-approvals/pending");
            setRows(r.data.pending || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleAction = async (row, kind) => {
        let reason = "";
        if (kind === "reject") {
            reason = window.prompt(`Reject ${row.email}? Add a note (visible in the rejection email + audit log):`, "") || "";
            if (reason === null) return;
        }
        const action = kind === "approve" ? "approve_employer" : "reject_employer";
        const label = kind === "approve" ? "Approved" : "Rejected";
        setBusyId(row.user_id);
        try {
            await api.post(`/admin/users/${row.user_id}/action`, { action, reason });
            toast.success(`${label} ${row.email}`);
            setRows((prev) => prev.filter((x) => x.user_id !== row.user_id));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div data-testid="admin-employer-approvals">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="overline">Command Center</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Employer approvals
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        LinkedIn-style workspace verification. Corporate signups whose
                        email domain matches their website are auto-approved; everything
                        else lands here for manual review.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="emp-approval-refresh"
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-60"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            <div className="mt-6">
                {loading ? (
                    <div className="py-16 flex justify-center">
                        <Loader2 size={18} className="animate-spin text-emerald-500" />
                    </div>
                ) : rows.length === 0 ? (
                    <div
                        data-testid="emp-approval-empty"
                        className="rounded-2xl border border-dashed border-border bg-card/60 p-12 text-center"
                    >
                        <Building2 className="mx-auto h-8 w-8 text-muted-foreground" />
                        <h3 className="mt-3 text-base font-semibold text-foreground">All clear</h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                            No employer accounts are waiting for approval right now.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {rows.map((row) => (
                            <EmployerCard
                                key={row.user_id}
                                row={row}
                                onAction={handleAction}
                                busy={busyId === row.user_id}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
